/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Button,
  ButtonGroup,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ReactNode, useState } from 'react';

import { useTranslation } from 'react-i18next';

interface ModalData {
  imagePath: string;
  title: string;
  description: string;
  confirmButtonText: string;
  children?: ReactNode;
}

interface AssignmentConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalType: 'unassign' | 'remind' | 'markCompleted' | 'duplicate' | 'delete' | null;
  onSubmit: () => void;
  submitLoading?: boolean;
}

export default function AssignmentConfirmationModal({
  isOpen,
  onClose,
  modalType,
  onSubmit,
  submitLoading = false,
}: AssignmentConfirmationModalProps) {
  const { t } = useTranslation();
  const [deleteInputText, setDeleteInputText] = useState('');

  const modalDataMap: Record<string, ModalData> = {
    unassign: {
      imagePath: '/public/images/add-files-E-commerce.svg',
      title: t('admin.assignments.unassignModal.title'),
      description: t('admin.assignments.unassignModal.description'),
      confirmButtonText: t('admin.assignments.unassignModal.confirmText'),
    },
    remind: {
      imagePath: '/public/images/Receive-Notification-2--Streamline-Brooklyn.svg',
      title: t('admin.assignments.remindModal.title'),
      description: t('admin.assignments.remindModal.description'),
      confirmButtonText: t('admin.assignments.remindModal.confirmText'),
    },
    markCompleted: {
      imagePath: '/public/images/Approval-2--Streamline-Brooklyn.svg',
      title: t('admin.assignments.completeModal.title'),
      description: t('admin.assignments.completeModal.description'),
      confirmButtonText: t('admin.assignments.completeModal.confirmText'),
    },
    duplicate: {
      imagePath: '/public/images/approval-4--work-approval.svg',
      title: t('admin.assignments.duplicateModal.title'),
      description: t('admin.assignments.duplicateModal.description'),
      confirmButtonText: t('admin.assignments.duplicateModal.confirmText'),
    },
    delete: {
      imagePath: '/public/images/Delete-3--Streamline-Brooklyn.svg',
      title: t('admin.assignments.deleteModal.title'),
      description: t('admin.assignments.deleteModal.description'),
      confirmButtonText: t('admin.assignments.deleteModal.confirmText'),

      children: (
        <Input
          variant="create"
          placeholder={`${t('admin.assignments.deleteModal.inputPlaceholder')}`}
          value={deleteInputText}
          onChange={(e) => setDeleteInputText(e.target.value)}
        />
      ),
    },
  };

  const { imagePath, title, description, confirmButtonText, children } = modalDataMap[modalType!];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />

      <ModalContent paddingTop={12} paddingX={10} paddingBottom={8}>
        <ModalBody>
          <VStack spacing={4} marginBottom={10} textAlign="center">
            <Image src={imagePath} alt={title} />

            <Text fontSize="20px" fontWeight={600}>
              {title}
            </Text>

            <Text fontSize="14px" fontWeight={500}>
              {description}
            </Text>
          </VStack>

          {children && children}
        </ModalBody>

        <ModalFooter>
          <ButtonGroup width="full" justifyContent="center">
            <Button variant="createCancel" onClick={onClose}>
              {t('global.actions.cancel')}
            </Button>

            <Button
              variant={modalType === 'delete' ? 'error' : 'solid'}
              isDisabled={modalType === 'delete' && deleteInputText !== 'DELETE'}
              onClick={onSubmit}
              isLoading={submitLoading}
            >
              {confirmButtonText}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
